import type { MetabaseTheme } from '@metabase/embedding-sdk-react'

import * as Colors from './colors'
import { getComputedColors } from './computed-colors'

// See the "Customizing appearance" section for more information
export const MetabaseLightTheme: MetabaseTheme = {
	// Specify a font to use from the set of fonts supported by Metabase.
	// You can set the font to "Custom" to use the custom font
	// configured in your Metabase instance.
	// fontFamily: 'Lato',
	fontFamily:
		'"Gotham",-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji"',

	// Override the base font size for every component.
	// This does not usually need to be set, as the components
	// inherit the font size from the parent container, such as the body.
	fontSize: '14px',

	// Override the base line height for every component.
	lineHeight: 1.5,

	// Match your application's color scheme
	colors: {
		// The primary color of your application

		// The primary color of your application
		brand: Colors.grassGreen[700],
		// brand: '#2ed3b8',

		// The color of text that is most prominent
		'text-primary': Colors.microGreen[900],

		// The color of text that is less prominent
		'text-secondary': Colors.grey[600],

		// The color of text that is least prominent
		'text-tertiary': '#949AAB',

		// Default background color
		background: '#FFFFFF',

		// Slightly darker background color used for hover and accented elements
		'background-hover': '#F9FBFC',

		// Color used for borders
		border: '#EEECEC',

		// Color used for filters context
		filter: '#FFFFFF',

		// Color used for aggregations and breakouts context
		summarize: '#88BF4D',

		// Color used to indicate successful actions and positive values/trends
		positive: '#BADC58',

		// Color used to indicate dangerous actions and negative values/trends
		negative: '#FF7979',

		/** Color used for popover shadows */
		shadow: 'rgba(0,0,0,0.08)',

		// Overrides the chart colors. Supports up to 8 colors
		// Limitation: this does not affect charts with custom series color
		charts: [
			// can either be a hex code
			// Colors.lightGreen[400],
			// or a color object. tint and shade represents lighter and darker variations
			// only base color is required, while tint and shade are optional
			// { base: '#139051', tint: Colors.lightGreen[200], shade: Colors.lightGreen[100] },
		],
	},

	components: {
		// Dashboard
		dashboard: {
			// Background color for all dashboards
			backgroundColor: '#fff',

			card: {
				// Background color for all dashboard cards
				backgroundColor: '#fff',

				// Apply a border color instead of shadow for dashboard cards.
				border: '1px solid #EEECEC',
			},
		},

		// Question
		question: {
			// Background color for all questions
			// backgroundColor: '#2D2D30',
			backgroundColor: '#fff',
		},

		// Data table
		table: {
			cell: {
				// Text color of cells, defaults to `text-primary`
				textColor: '#4C5773',

				// Default background color of cells, defaults to `background`
				backgroundColor: '#FFFFFF',

				// Font size of cell values, defaults to ~12.5px
				fontSize: '12.5px',
			},

			idColumn: {
				// Text color of ID column, defaults to `brand`
				textColor: '#9B5966',

				// Background color of ID column, defaults to a lighter shade of `brand`
				backgroundColor: '#FFFFFF',
			},
		},

		// Number chart
		number: {
			// Value displayed on number charts.
			// This also applies to the primary value in trend charts.
			value: {
				fontSize: '24px',
				lineHeight: '21px',
			},
		},

		// Cartesian chart
		cartesian: {
			// Padding around the cartesian charts.
			// Uses CSS's `padding` property format.
			padding: '12px 18px',
		},

		// Pivot table
		pivotTable: {
			cell: {
				// Font size of cell values, defaults to ~12px
				fontSize: '12px',
			},

			// Pivot row toggle to expand or collapse row
			rowToggle: {
				textColor: '#FFFFFF',
				backgroundColor: '#95A5A6',
			},
		},

		collectionBrowser: {
			breadcrumbs: {
				expandButton: {
					textColor: '#8118F4',
					// backgroundColor: '#767D7C',
					backgroundColor: '#fff',
					hoverTextColor: '#CE8C8C',
					hoverBackgroundColor: '#69264B',
				},
			},
		},

		// Popover are used in components such as click actions in interactive questions.
		popover: {
			// z-index must be above the side nav (1100) so popovers aren't clipped
			zIndex: 1200,
		},
	},
}

export const getMetabaseDarkTheme = (): MetabaseTheme => {
	// NOTE: using var(--mui-palette-neutral-950) broke with latest Metabase SDK
	// so now we're computing them in JS instead
	const ComputedColors = getComputedColors()

	return {
		...MetabaseLightTheme,
		colors: {
			...MetabaseLightTheme.colors,
			background: ComputedColors.neutral950,
			'text-primary': '#fff' /* NOTE: This seems to apply globally, not just to metabase table */,
			'text-secondary': ComputedColors.neutral400,
			border: ComputedColors.neutral700,
			shadow: 'rgba(255,255,255,0.1)',
		},
		components: {
			...MetabaseLightTheme.components,
			dashboard: {
				...MetabaseLightTheme.components?.dashboard,
				backgroundColor: ComputedColors.neutral950,
				card: {
					backgroundColor: ComputedColors.backgroundPaper,
				},
			},
			question: {
				...MetabaseLightTheme.components?.question,
				backgroundColor: ComputedColors.backgroundPaper,
			},
			table: {
				...MetabaseLightTheme.components?.table,
				cell: {
					...MetabaseLightTheme.components?.table?.cell,
					textColor: Colors.grey[400],
					backgroundColor: ComputedColors.backgroundPaper,
				},
				idColumn: {
					...MetabaseLightTheme.components?.table?.idColumn,
					textColor: '#9B5966',
					backgroundColor: ComputedColors.backgroundPaper,
				},
			},
			pivotTable: {
				...MetabaseLightTheme.components?.pivotTable,
				// Pivot row toggle to expand or collapse row
				rowToggle: {
					...MetabaseLightTheme.components?.pivotTable?.rowToggle,
					textColor: '#FFFFFF',
					backgroundColor: ComputedColors.backgroundPaper,
				},
			},
			collectionBrowser: {
				breadcrumbs: {
					expandButton: {
						...MetabaseLightTheme.components?.collectionBrowser?.breadcrumbs?.expandButton,
						textColor: '#8118F4',
						backgroundColor: ComputedColors.backgroundPaper,
						hoverTextColor: '#CE8C8C',
						hoverBackgroundColor: ComputedColors.backgroundPaper,
					},
				},
			},
		},
	}
}
